import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  Upload, 
  Trash2, 
  MapPin, 
  Globe, 
  FileSpreadsheet, 
  ChevronLeft, 
  ChevronRight 
} from "lucide-react";

const StationPage = () => {
  const [stations, setStations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // --- 1. STATE UNTUK PAGINATION ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100; // Menampilkan 100 data per halaman

  const API_URL = "/api";

  useEffect(() => {
    fetchStations();
  }, []);

  // Reset ke halaman 1 jika user melakukan pencarian
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchStations = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/stations`);
      setStations(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Gagal mengambil data station");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setIsUploading(true);
    const loadToast = toast.loading("Sedang mengupload data station...");

    try {
      const res = await axios.post(`${API_URL}/stations/upload`, formData);
      toast.dismiss(loadToast);
      toast.success(res.data.message || "Upload berhasil!");
      fetchStations(); 
    } catch (error) {
      toast.dismiss(loadToast);
      toast.error(error.response?.data?.detail || "Gagal upload file");
    } finally {
      setIsUploading(false);
      e.target.value = null; 
    }
  };

  const handleDelete = async (code, name) => {
    if (!window.confirm(`Yakin ingin menghapus station ${name} (${code})?`)) return;

    try {
      await axios.delete(`${API_URL}/stations/${code}`);
      toast.success("Station berhasil dihapus");
      setStations(prev => prev.filter(s => s.city_code !== code));
    } catch (error) {
      toast.error("Gagal menghapus station");
    }
  };

  // --- FILTER PENCARIAN ---
  const filteredStations = stations.filter(s => 
    s.city_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.city_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.country_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- 2. LOGIKA PEMBAGIAN HALAMAN (SLICING) ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStations = filteredStations.slice(indexOfFirstItem, indexOfLastItem);
  
  const totalPages = Math.ceil(filteredStations.length / itemsPerPage);

  // Fungsi ganti halaman
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      
      {/* HEADER & ACTIONS */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="text-blue-600" /> Master Station
          </h1>
          <p className="text-gray-500 mt-1">Database kode kota, negara, dan mapping zona.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {/* SEARCH BAR */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Cari Kota / Kode / Negara..." 
              className="pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none w-full sm:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* UPLOAD BUTTON */}
          <div className="relative">
            <input 
              type="file" 
              accept=".xlsx, .xls"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={handleUpload}
              disabled={isUploading}
            />
            <button className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-white transition shadow-sm ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}>
              <Upload size={18} />
              {isUploading ? 'Uploading...' : 'Upload Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* TABLE CONTENT */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 text-sm font-bold text-gray-700">No.</th> {/* Tambah kolom No */}
                <th className="px-6 py-4 text-sm font-bold text-gray-700">City Code</th>
                <th className="px-6 py-4 text-sm font-bold text-gray-700">City Name</th>
                <th className="px-6 py-4 text-sm font-bold text-gray-700">Country</th>
                <th className="px-6 py-4 text-sm font-bold text-gray-700 text-center">Zone</th>
                <th className="px-6 py-4 text-sm font-bold text-gray-700">Description</th>
                <th className="px-6 py-4 text-sm font-bold text-gray-700 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-500">Sedang memuat data...</td></tr>
              ) : filteredStations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                        <FileSpreadsheet className="text-gray-300 mb-2" size={48} />
                        <p>Belum ada data station.</p>
                        <p className="text-sm text-gray-400">Silakan upload file Excel Master Station Anda.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                // --- 3. RENDER DATA YANG SUDAH DIPOTONG (CurrentStations) ---
                currentStations.map((station, index) => (
                  <tr key={station.city_code} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-gray-500 text-sm">
                        {/* Logic Nomor Urut: (Halaman - 1) * 100 + index + 1 */}
                        {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono font-bold bg-blue-50 text-blue-700 px-2 py-1 rounded">
                        {station.city_code}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{station.city_name}</td>
                    <td className="px-6 py-4 text-gray-600 flex items-center gap-2">
                        <Globe size={14} className="text-gray-400" />
                        {station.country_name}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 font-bold text-gray-700 border border-gray-200">
                        {station.zone}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">
                        {station.description || "-"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => handleDelete(station.city_code, station.city_name)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition"
                        title="Hapus Station"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* --- 4. PAGINATION CONTROLS (FOOTER) --- */}
        {!isLoading && filteredStations.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
                Menampilkan <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, filteredStations.length)}</strong> dari <strong>{filteredStations.length}</strong> data
            </div>

            <div className="flex items-center gap-2">
                <button 
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600"
                >
                    <ChevronLeft size={16} />
                </button>
                
                <span className="text-sm font-medium text-gray-700 px-2">
                    Halaman {currentPage} dari {totalPages}
                </span>

                <button 
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StationPage;